import { useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    Collapse,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import BuildIcon from '@mui/icons-material/Build';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FlagIcon from '@mui/icons-material/Flag';
import ListViewTableBodyRow from '../../../../Shared/Components/ListViewTable/ListViewTableBodyRow';
import ListViewTableBodyCell from '../../../../Shared/Components/ListViewTable/ListViewTableBodyCell';
import { MIN_COLUMN_WIDTH, TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED } from '../styles';
import {
    currencyFormatter,
    convertBase64ToBlob,
    getSalesforceUserLink,
} from '../../../../utils/helpers';
import CellLink from '../../ProcessedTimesheets/Table/CellLink';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';
import {
    SelectionStatus,
    useInvoicingSelectionContext,
} from '../../../Contexts/InvoicingSelectionContext';
import AnimatedExpandIcon from '../../../../Shared/Components/AnimatedExpandIcon';
import useHierarchyConnectorVertical from '../hooks/useHierarchyConnectorVertical';
import { formatUtcDate } from '../../../../utils/formatDate';
import InvoicingItemContainer from './InvoicingItemContainer';
import StatusPill from '../StatusPill';
import InvoiceIssuesBanner from './InvoiceIssuesBanner';
import { getInvoicePdf } from '../../../Services/InvoiceService';
import { isBookingSelectable } from '../../../Configurations/invoicingConfig';
import { useToast } from '../../../Contexts/ToastContext';
import useAdminAuthContext from '../../../hooks/useAdminAuthContext';

const InvoiceContainerCollapse = ({ invoice, disableSelection = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const toggleExpand = () => setIsExpanded((prev) => !prev);
    const { showErrorToast } = useToast();

    const { isInvoicingSystemAdminUser } = useAdminAuthContext();
    const {
        lastChildRef,
        expandIconRef,
        hierarchyConnectorVerticalRef,
        mainContainerRef,
        renderConnector,
    } = useHierarchyConnectorVertical();

    // Values now provided by adapter
    const items = Array.isArray(invoice?.items) ? invoice.items : [];
    const normalizedIssues = Array.isArray(invoice?.issues) ? invoice.issues : [];
    const bookingNumbers = Array.isArray(invoice?.bookingNumberBadges)
        ? invoice.bookingNumberBadges
        : [{ id: 'unknown', text: 'Unknown' }];
    const displayType = invoice?.displayType || '';

    const hasErr = Boolean(invoice?.hasErrors);
    const hasWarn = Boolean(invoice?.hasWarnings);
    const issuesCount = Number.isFinite(invoice?.issuesCount)
        ? invoice.issuesCount
        : normalizedIssues.length;
    const hasSubItems = items.length > 0;

    // allow expand if it has sub-items OR there are invoice-level issues (errors or warnings)
    const canExpand = hasSubItems || ((hasErr || hasWarn) && issuesCount > 0);

    // Use booking-based APIs from the context
    const { getBookingSelectionState, handleSelectBooking } = useInvoicingSelectionContext();
    const selectionId = invoice.bookingId ?? invoice.id;
    const invoiceSelectionStatus = getBookingSelectionState(selectionId);

    const handleGetInvoicePdf = async () => {
        try {
            setIsPdfLoading(true);
            const result = await getInvoicePdf(invoice.bookingId);
            const file = convertBase64ToBlob(result.base64Data, 'application/pdf');
            window.open(URL.createObjectURL(file));
        } catch (error) {
            console.error(`Error opening pdf. ${error}`);
            showErrorToast('Failed to load invoice PDF. Please try again later.', {
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            });
        } finally {
            setIsPdfLoading(false);
        }
    };

    const isSelectable =
        isInvoicingSystemAdminUser && isBookingSelectable(invoice) && !disableSelection;

    return (
        <Box
            ref={mainContainerRef}
            sx={
                isExpanded
                    ? { backgroundColor: '#F5F6F7', pb: '10px', borderRadius: '5px' }
                    : undefined
            }
            position="relative"
        >
            <ListViewTableBodyRow
                sx={{ cursor: canExpand ? 'pointer' : 'default', height: 80 }}
                onClick={canExpand ? toggleExpand : undefined}
                noBorder={isExpanded}
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? `invoice-items-collapse-${selectionId}` : undefined}
            >
                {/* First column: expand chevron, error/warning icon, checkbox */}
                <ListViewTableBodyCell
                    sx={{
                        width: '90px',
                        pl: '16px !important',
                        pr: '2.5px !important',
                        flexGrow: '0 !important',
                        justifyContent: 'space-between',
                    }}
                >
                    {canExpand && (
                        <AnimatedExpandIcon ref={expandIconRef} isExpanded={isExpanded} />
                    )}

                    {isSelectable && (
                        <Checkbox
                            sx={{ p: 1, ml: !canExpand ? 'auto' : undefined }}
                            checked={invoiceSelectionStatus === SelectionStatus.SELECTED}
                            indeterminate={invoiceSelectionStatus === SelectionStatus.INDETERMINATE}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectBooking(selectionId);
                            }}
                        />
                    )}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={1.7}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                    }}
                >
                    {invoice.clientName}
                    <Box
                        flexDirection="row"
                        display="flex"
                        marginLeft="auto"
                        sx={{
                            pl:
                                invoice.isCorrection || hasErr || hasWarn || invoice.isHighPriority
                                    ? 1
                                    : 0,
                        }}
                    >
                        {invoice.isCorrection && (
                            <Tooltip title="This invoice has corrections">
                                <BuildIcon
                                    sx={{
                                        color: COLORS.LT_POMEGRANATE,
                                        verticalAlign: 'middle',
                                        height: 17,
                                        width: 17,
                                        mr: 0.5,
                                    }}
                                />
                            </Tooltip>
                        )}
                        {invoice.isHighPriority && (
                            <Tooltip title="High Priority" placement="top" arrow>
                                <FlagIcon
                                    sx={{
                                        color: COLORS.LT_PEPPER125,
                                        mr: 0.5,
                                        fontSize: 18,
                                        verticalAlign: 'middle',
                                    }}
                                />
                            </Tooltip>
                        )}
                        {(hasErr || hasWarn) && issuesCount > 0 && (
                            <Tooltip
                                title={(hasErr ? 'Errors: ' : 'Warnings: ') + String(issuesCount)}
                                placement="top"
                                arrow
                            >
                                {hasErr ? (
                                    <ErrorOutlineIcon
                                        sx={{
                                            color: '#D93025',
                                            mr: 0.5,
                                            fontSize: 18,
                                            verticalAlign: 'middle',
                                        }}
                                    />
                                ) : (
                                    <WarningAmberIcon
                                        sx={{
                                            color: '#B86E00',
                                            mr: 1.25,
                                            fontSize: 18,
                                            verticalAlign: 'middle',
                                        }}
                                    />
                                )}
                            </Tooltip>
                        )}
                    </Box>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={0.6}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                        whiteSpace: 'normal',
                        justifyContent: 'left',
                    }}
                >
                    {invoice.customerId}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={0.8}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED }}
                >
                    {invoice.invoiceNumber}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={1.2}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                textWrap: 'wrap !important',
                            }}
                        >
                            {invoice.clinicianName}
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                fontSize: 12,
                            }}
                        >
                            Locations: {invoice.numberOfLocations}
                        </Typography>
                    </Stack>
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={0.8}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                    }}
                >
                    <div>
                        {bookingNumbers.map((item, i) => (
                            <div key={item.id}>
                                {item.text}
                                {i < bookingNumbers.length - 1 ? ';' : ''}
                            </div>
                        ))}
                    </div>
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={0.8}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                    }}
                >
                    {formatUtcDate(invoice.invoiceDate, DATE_FORMATS.US_DATE)}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={0.8}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                    }}
                >
                    {formatUtcDate(invoice.accountingDate, DATE_FORMATS.US_DATE)}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell size={1} sx={{ minWidth: 130 }}>
                    {invoice.status ? <StatusPill status={invoice.status} /> : '-'}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={0.8}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                        fontWeight: 600,
                    }}
                >
                    {currencyFormatter(invoice.amount)}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell size={1}>
                    <Typography
                        sx={{
                            ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                            color: COLORS.LT_MIDNIGHT,
                        }}
                    >
                        {invoice.collectorSfId && invoice.collectorName ? (
                            <CellLink
                                href={getSalesforceUserLink(invoice.collectorSfId)}
                                label={invoice.collectorName}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        ) : (
                            invoice.collectorName
                        )}
                    </Typography>
                </ListViewTableBodyCell>

                <ListViewTableBodyCell
                    size={0.5}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                    }}
                >
                    {displayType}
                </ListViewTableBodyCell>

                <ListViewTableBodyCell size={0.5}>
                    {invoice.status !== 'Errored' && invoice.fileName && (
                        <IconButton onClick={handleGetInvoicePdf} disabled={isPdfLoading}>
                            {isPdfLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <PictureAsPdfIcon sx={{ color: COLORS.LT_MIDNIGHT50 }} />
                            )}
                        </IconButton>
                    )}
                </ListViewTableBodyCell>
            </ListViewTableBodyRow>

            <Collapse in={isExpanded} id={`invoice-items-collapse-${selectionId}`}>
                {hasSubItems && renderConnector(isExpanded)}
                {/* Invoice-level issues banner (top of the expanded area) */}
                {(hasErr || hasWarn) && (
                    <Box sx={{ ml: '106px', mr: '16px', mt: 1 }}>
                        <InvoiceIssuesBanner
                            issues={normalizedIssues}
                            hasErrors={hasErr}
                            hasWarnings={hasWarn}
                        />
                    </Box>
                )}
                <Box sx={{ ml: '56px' }}>
                    {items.map((item, index) => (
                        <InvoicingItemContainer
                            key={`inv:${selectionId}|${item.type}:${item.id}`}
                            invoiceId={selectionId}
                            type={item.type}
                            itemId={item.id}
                            data={item.data}
                            selectable={false}
                            hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                            ref={index === items.length - 1 ? lastChildRef : null}
                            // pass normalized issues/flags to render banner inside the collapse
                            issues={normalizedIssues}
                            hasErrors={hasErr}
                            hasWarnings={hasWarn}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export default InvoiceContainerCollapse;
